import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { useSelector } from "react-redux";
import { useMaterialUIController } from "../../context";
import SimpleMap from "../../layouts/dashboard/components/SimpleMap";
import { GetMapReportCall, GetStudentsByCountryCall } from "../../redux/api/report.api";

function normalizeRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
}

/**
 * World map of users/students by country.
 * - admin (default): platform `/report/dashboard-map`
 * - university_admin / prof: scoped `/report/students-by-country`
 * Pass `scoped` to force the students endpoint.
 */
export default function AdminStatsMap({ scoped = false }) {
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const role = normalizeRole(useSelector((state) => state?.auth?.role));
  const mapReport = useSelector((state) => state?.report?.map);
  const [localData, setLocalData] = useState(null);
  const [loading, setLoading] = useState(true);

  const useStudentsEndpoint =
    scoped || role === "university_admin" || role === "university" || role === "prof" || role === "professor";

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const load = async () => {
      if (useStudentsEndpoint) {
        const res = await GetStudentsByCountryCall();
        if (mounted) {
          setLocalData(res?.users_by_country ?? res?.data?.users_by_country ?? null);
          setLoading(false);
        }
        return;
      }
      await GetMapReportCall();
      if (mounted) setLoading(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [useStudentsEndpoint]);

  const data = useStudentsEndpoint
    ? localData
    : mapReport?.data?.users_by_country ?? mapReport?.users_by_country;

  if (loading || data == null) {
    return (
      <Skeleton
        variant="rounded"
        height={420}
        sx={{ borderRadius: 2, maxWidth: "100%" }}
      />
    );
  }

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#475569" }}>
        {useStudentsEndpoint
          ? "No student country data yet. Students need a country on their profile."
          : "No map data available yet."}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: darkMode ? "#0c0f14" : "background.default",
        minHeight: { xs: 360, md: 420 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", px: { xs: 0, sm: 1 }, py: 1 }}>
        <SimpleMap dataOverride={useStudentsEndpoint ? data : undefined} />
      </Box>
    </Box>
  );
}
